import { useState, useEffect, useMemo } from 'react';
import { useExtension, useDialLists, useAdminGroups } from '../../lib/api/hooks-extension.js';
import { useUpdateExtension } from '../../lib/api/hooks.js';
import { useExtensionTypes } from '../../lib/api/hooks-products.js';
import { Icon } from '../../components/ui/Icon.js';
import { StatusBadge, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Section, Field, Toggle, FormActions } from '../../components/ui/Form.js';
import { ApiClientError } from '../../lib/api/client.js';
import { VoicemailTab } from './ExtensionVoicemail.js';
import { MembershipTab, RegistrationTab } from './ExtensionTabs.js';
import type { Extension } from '@arcnet/contracts';

type Tab = 'details' | 'restrictions' | 'recording' | 'voicemail' | 'membership' | 'registration';

const TABS: Array<[Tab, string]> = [
  ['details', 'Details'],
  ['restrictions', 'Dialling restrictions'],
  ['recording', 'Recording'],
  ['voicemail', 'Voicemail'],
  ['membership', 'Groups & queues'],
  ['registration', 'Registration'],
];

/**
 * One extension, everything about it.
 *
 * A seat is not a number and a name: it carries outbound presentation, cost
 * attribution, dialling restrictions, recording policy and supervision rules,
 * most of which cost money or carry compliance weight when set wrongly. Split
 * across tabs so the common case (name, caller ID) stays on one screen and the
 * rarely-touched controls do not crowd it.
 */
export function ExtensionDetail({ tenantId, extensionId, onBack }: {
  tenantId: string; extensionId: string; onBack: () => void;
}) {
  const [tab, setTab] = useState<Tab>('details');
  const ext = useExtension(tenantId, extensionId, tab === 'registration');

  if (ext.error) return <ErrorBanner error={ext.error} />;

  return (
    <>
      <button type="button" className="arc-crumb"
        onClick={onBack}
        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
        <Icon name="arrowLeft" size={13} /> Extensions
      </button>

      <div className="arc-page-head">
        <div>
          <div className="arc-row" style={{ gap: 9 }}>
            <h1 className="arc-h1">
              <span className="arc-mono">{ext.data?.number}</span>
              {ext.data ? ` · ${ext.data.display_name}` : ''}
            </h1>
            {/* Only when it is NOT active. "Active" is the state every seat is
                in, so a pill saying so is a pill that is always there and never
                read -- and it makes the one that matters, a suspended seat,
                look like more of the same. Same rule the list follows. */}
            {ext.data && ext.data.status !== 'active' && (
              <StatusBadge status={ext.data.status} />
            )}
            {ext.data?.extension_type_name && (
              <Badge tone="info">{ext.data.extension_type_name}</Badge>
            )}
          </div>
        </div>
      </div>

      <div className="arc-tabs" role="tablist">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" role="tab" className="arc-tab"
            data-active={tab === k} onClick={() => setTab(k)}>
            {label}
          </button>
        ))}
      </div>

      {ext.isLoading || !ext.data ? (
        <div className="arc-card arc-card-body">
          <div className="arc-skeleton" style={{ height: 90 }} />
        </div>
      ) : (
        <>
          {tab === 'details'      && <DetailsTab tenantId={tenantId} ext={ext.data} />}
          {tab === 'restrictions' && <RestrictionsTab tenantId={tenantId} ext={ext.data} />}
          {tab === 'recording'    && <RecordingTab tenantId={tenantId} ext={ext.data} />}
          {tab === 'voicemail'    && <VoicemailTab tenantId={tenantId} ext={ext.data} />}
          {tab === 'membership'   && <MembershipTab tenantId={tenantId} ext={ext.data} />}
          {tab === 'registration' && <RegistrationTab tenantId={tenantId} ext={ext.data} />}
        </>
      )}
    </>
  );
}

/**
 * Tracks a draft against the server copy and reports whether it differs.
 *
 * Only CHANGED keys are sent: a PATCH that echoes every field back would
 * clobber a concurrent edit by another operator with values this form never
 * touched.
 */
function useDraft<T extends Record<string, unknown>>(source: T) {
  const [draft, setDraft] = useState<Partial<T>>({});
  useEffect(() => { setDraft({}); }, [source]);

  const set = <K extends keyof T>(key: K, value: T[K]) =>
    setDraft((d) => {
      const next = { ...d };
      if (Object.is(source[key], value)) delete next[key];
      else next[key] = value;
      return next;
    });

  const value = <K extends keyof T>(key: K): T[K] =>
    (key in draft ? draft[key] : source[key]) as T[K];

  const dirty = Object.keys(draft).length > 0;
  return { draft, set, value, dirty, reset: () => setDraft({}) };
}

function DetailsTab({ tenantId, ext }: { tenantId: string; ext: Extension }) {
  const save = useUpdateExtension(tenantId);
  const types = useExtensionTypes();
  const groups = useAdminGroups(tenantId);
  const d = useDraft(ext as unknown as Record<string, unknown>);
  const err = save.error instanceof ApiClientError ? save.error : null;

  const str = (k: string) => (d.value(k) as string | null) ?? '';
  const bool = (k: string) => Boolean(d.value(k));

  return (
    <>
      {err && <ErrorBanner error={err} />}

      <Section title="Identity" description="How this extension appears internally.">
        <Field label="Display name" htmlFor="d-name">
          <input id="d-name" className="arc-input" value={str('display_name')}
            onChange={(e) => d.set('display_name', e.target.value)} />
        </Field>
        <Field label="Extension number" htmlFor="d-number"
          hint="Shared dial space — hunt lists, queues and IVRs use the same range.">
          <input id="d-number" className="arc-input arc-mono" value={str('number')}
            onChange={(e) => d.set('number', e.target.value)} />
        </Field>
        <Field label="Seat type" htmlFor="d-type"
          hint="Determines the feature set and the monthly charge.">
          <select id="d-type" className="arc-select" value={str('extension_type_id')}
            onChange={(e) => d.set('extension_type_id', e.target.value || null)}>
            <option value="">— none —</option>
            {types.data?.data.filter((t) => t.is_active).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Language" htmlFor="d-lang" hint="Prompt language for this seat.">
          <input id="d-lang" className="arc-input" value={str('language')}
            placeholder="en-ZA" onChange={(e) => d.set('language', e.target.value || null)} />
        </Field>
      </Section>

      <Section title="Outbound presentation"
        description="What the far end sees when this extension calls out.">
        <Field label="Caller ID number" htmlFor="d-cid">
          <input id="d-cid" className="arc-input arc-mono" value={str('outbound_caller_id_number')}
            placeholder="+27115550100"
            onChange={(e) => d.set('outbound_caller_id_number', e.target.value || null)} />
        </Field>
        <Field label="Caller ID name" htmlFor="d-cidname">
          <input id="d-cidname" className="arc-input" value={str('outbound_caller_id_name')}
            onChange={(e) => d.set('outbound_caller_id_name', e.target.value || null)} />
        </Field>
        <Field label="Emergency caller ID" htmlFor="d-ecid"
          hint="Presented on emergency calls. Must be a number tied to this site's address (doc 22).">
          <input id="d-ecid" className="arc-input arc-mono"
            value={str('emergency_caller_id_number')} placeholder="+27115550100"
            onChange={(e) => d.set('emergency_caller_id_number', e.target.value || null)} />
        </Field>
        <Toggle
          id="d-hide" label="Withhold caller ID"
          hint="Presents the call as private. Many carriers reject anonymous calls to emergency numbers."
          checked={bool('hide_caller_id')}
          onChange={(v) => d.set('hide_caller_id', v)}
        />
      </Section>

      <Section title="Cost attribution" description="Where this seat's usage is reported.">
        <Field label="Account / agent code" htmlFor="d-acct"
          hint="Appears on the CDR for cost-centre reporting.">
          <input id="d-acct" className="arc-input" value={str('account_code')}
            onChange={(e) => d.set('account_code', e.target.value || null)} />
        </Field>
        <Field label="Department" htmlFor="d-group">
          <select id="d-group" className="arc-select" value={str('admin_group_id')}
            onChange={(e) => d.set('admin_group_id', e.target.value || null)}>
            <option value="">— none —</option>
            {groups.data?.data.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Spend cap" htmlFor="d-budget"
          hint="Outbound calls are refused once exceeded.">
          <input id="d-budget" className="arc-input" inputMode="decimal"
            value={str('budget_amount')} placeholder="0.00"
            onChange={(e) => d.set('budget_amount', e.target.value || null)} />
        </Field>
        <Field label="Cap period" htmlFor="d-period">
          <select id="d-period" className="arc-select" value={str('budget_period')}
            onChange={(e) => d.set('budget_period', e.target.value || null)}>
            <option value="">— no cap —</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </Field>
      </Section>

      <Section title="Capability & behaviour">
        <Field label="Phone engine" htmlFor="d-engine"
          hint="How this seat connects: a physical handset, the browser, or a mobile app.">
          <select id="d-engine" className="arc-select" value={str('phone_engine')}
            onChange={(e) => d.set('phone_engine', e.target.value)}>
            <option value="external">External handset</option>
            <option value="webrtc">Browser (WebRTC)</option>
            <option value="mobile">Mobile app</option>
            <option value="softphone">Desktop softphone</option>
          </select>
        </Field>
        <Field label="No-answer timeout" htmlFor="d-timeout" hint="Seconds before the call moves on.">
          <input id="d-timeout" className="arc-input" inputMode="numeric"
            value={String(d.value('call_timeout_sec') ?? 30)}
            onChange={(e) => d.set('call_timeout_sec', Number(e.target.value) || 30)} />
        </Field>
        <Field label="Concurrent call limit" htmlFor="d-max">
          <input id="d-max" className="arc-input" inputMode="numeric"
            value={String(d.value('max_concurrent_calls') ?? 4)}
            onChange={(e) => d.set('max_concurrent_calls', Number(e.target.value) || 1)} />
        </Field>
        <div />
        <Toggle id="d-video" label="Video calling" checked={bool('video_enabled')}
          onChange={(v) => d.set('video_enabled', v)} />
        <Toggle id="d-fax" label="Fax (T.38)"
          hint="Enables fax passthrough. Leave off unless a fax machine is attached — it changes codec negotiation."
          checked={bool('fax_enabled')} onChange={(v) => d.set('fax_enabled', v)} />
        <Toggle id="d-dir" label="Show in company directory"
          checked={bool('directory_visible')} onChange={(v) => d.set('directory_visible', v)} />
        <Toggle id="d-dnd" label="Do not disturb"
          checked={bool('do_not_disturb')} onChange={(v) => d.set('do_not_disturb', v)} />
      </Section>

      <Section title="Supervision & security">
        <Toggle
          id="d-spy" label="Prevent monitoring"
          hint="Blocks whisper, listen and barge on this extension. Required for HR, legal and director lines."
          checked={bool('prevent_spy')} onChange={(v) => d.set('prevent_spy', v)}
        />
        <Toggle
          id="d-auth" label="Require PIN for outbound calls"
          hint="For shared handsets in public areas — the caller must identify themselves before dialling out."
          checked={bool('require_authentication')}
          onChange={(v) => d.set('require_authentication', v)}
        />
      </Section>

      <FormActions
        dirty={d.dirty} saving={save.isPending}
        error={err && Object.keys(err.fieldErrors).length === 0 ? err.message : null}
        onReset={d.reset}
        onSave={() => save.mutate({ id: ext.id, ...d.draft }, { onSuccess: d.reset })}
      />
    </>
  );
}

function RestrictionsTab({ tenantId, ext }: { tenantId: string; ext: Extension }) {
  const save = useUpdateExtension(tenantId);
  const lists = useDialLists(tenantId);
  const d = useDraft(ext as unknown as Record<string, unknown>);
  const err = save.error instanceof ApiClientError ? save.error : null;

  const byKind = (kind: string) => (lists.data?.data ?? []).filter((l) => l.kind === kind);
  const str = (k: string) => (d.value(k) as string | null) ?? '';
  const bool = (k: string) => Boolean(d.value(k));
  const internalOnly = bool('internal_only');

  return (
    <>
      {err && <ErrorBanner error={err} />}

      <div className="arc-banner arc-banner-info" style={{ marginBottom: 14 }}>
        <Icon name="warning" size={14} />
        <div>
          <div style={{ fontWeight: 600 }}>Restrictions are evaluated in order</div>
          <div style={{ fontSize: 12 }}>
            Internal-only wins over everything. Then the block list, then the allow list,
            then the country list. A destination the block list matches is refused even
            if the allow list also matches it.
          </div>
        </div>
      </div>

      <Section title="Scope" columns={1}>
        <Toggle
          id="r-internal" label="Internal calls only"
          hint="This seat can reach other extensions and emergency numbers, nothing else. The strongest control available, and the right default for lobby and warehouse phones."
          checked={internalOnly} onChange={(v) => d.set('internal_only', v)}
        />
        <Toggle
          id="r-intl" label="Allow international calls"
          hint="Off by default. International destinations are where toll fraud monetises."
          checked={bool('international_allowed')} disabled={internalOnly}
          onChange={(v) => d.set('international_allowed', v)}
        />
      </Section>

      <Section title="Pattern lists"
        description="Shared lists, edited once and applied to every extension that uses them.">
        <Field label="Block list" htmlFor="r-block"
          hint="Refused outright. Premium-rate and satellite ranges live here by default.">
          <select id="r-block" className="arc-select" value={str('block_list_id')}
            disabled={internalOnly}
            onChange={(e) => d.set('block_list_id', e.target.value || null)}>
            <option value="">— none —</option>
            {byKind('block').map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}{l.is_platform_default ? ' (platform default)' : ''} · {l.patterns.length} patterns
              </option>
            ))}
          </select>
        </Field>
        <Field label="Allow list" htmlFor="r-allow"
          hint="Explicit exceptions. Does not override the block list.">
          <select id="r-allow" className="arc-select" value={str('allow_list_id')}
            disabled={internalOnly}
            onChange={(e) => d.set('allow_list_id', e.target.value || null)}>
            <option value="">— none —</option>
            {byKind('allow').map((l) => (
              <option key={l.id} value={l.id}>{l.name} · {l.patterns.length} patterns</option>
            ))}
          </select>
        </Field>
        <Field label="Country list" htmlFor="r-country" span
          hint="Countries reachable at all. Empty means the platform default.">
          <select id="r-country" className="arc-select" value={str('country_list_id')}
            disabled={internalOnly}
            onChange={(e) => d.set('country_list_id', e.target.value || null)}>
            <option value="">— platform default —</option>
            {byKind('country').map((l) => (
              <option key={l.id} value={l.id}>{l.name} · {l.patterns.join(', ')}</option>
            ))}
          </select>
        </Field>
      </Section>

      <FormActions
        dirty={d.dirty} saving={save.isPending}
        error={err && Object.keys(err.fieldErrors).length === 0 ? err.message : null}
        onReset={d.reset}
        onSave={() => save.mutate({ id: ext.id, ...d.draft }, { onSuccess: d.reset })}
      />
    </>
  );
}

function RecordingTab({ tenantId, ext }: { tenantId: string; ext: Extension }) {
  const save = useUpdateExtension(tenantId);
  const d = useDraft(ext as unknown as Record<string, unknown>);
  const err = save.error instanceof ApiClientError ? save.error : null;
  const mode = (d.value('record_mode') as string) ?? 'none';

  const MODES: Array<[string, string, string]> = [
    ['none', 'Not recorded', 'No calls on this extension are captured.'],
    ['all', 'All calls', 'Both directions. The usual setting for contact-centre seats.'],
    ['inbound', 'Inbound only', 'Calls received by this extension.'],
    ['outbound', 'Outbound only', 'Calls placed from this extension.'],
    ['on_demand', 'On demand', 'The user starts recording with a feature code mid-call.'],
  ];

  return (
    <>
      {err && <ErrorBanner error={err} />}

      <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
        <Icon name="warning" size={14} />
        <div>
          <div style={{ fontWeight: 600 }}>Recording carries legal obligations</div>
          <div style={{ fontSize: 12 }}>
            Under RICA the parties must be aware, and under POPIA a recording is personal
            information with a retention limit and a subject-access right. Set retention
            deliberately — indefinite storage is a liability, not a safety net (doc 22).
          </div>
        </div>
      </div>

      <Section title="What gets recorded" columns={1}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {MODES.map(([v, label, hint]) => (
            <label key={v} className="arc-row"
              style={{ gap: 9, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="radio" name="rec-mode" value={v} checked={mode === v}
                onChange={() => d.set('record_mode', v)} style={{ marginTop: 2 }} />
              <div className="arc-stack">
                <span style={{ fontSize: 12.5, fontWeight: 500 }}>{label}</span>
                <span className="arc-hint" style={{ marginTop: 0 }}>{hint}</span>
              </div>
            </label>
          ))}
        </div>
      </Section>

      <Section title="Retention">
        <Field label="Keep recordings for" htmlFor="rec-days"
          hint="Days. Blank inherits the account default. Recordings are deleted permanently once past this.">
          <input id="rec-days" className="arc-input" inputMode="numeric"
            disabled={mode === 'none'}
            value={String(d.value('record_retention_days') ?? '')}
            placeholder="account default"
            onChange={(e) => d.set('record_retention_days',
              e.target.value ? Number(e.target.value) : null)} />
        </Field>
      </Section>

      <FormActions
        dirty={d.dirty} saving={save.isPending}
        error={err && Object.keys(err.fieldErrors).length === 0 ? err.message : null}
        onReset={d.reset}
        onSave={() => save.mutate({ id: ext.id, ...d.draft }, { onSuccess: d.reset })}
      />
    </>
  );
}
